/**
 * API Client — Comunicação com backend para PWA Participante.
 *
 * Adiciona Authorization header automaticamente.
 * Trata erros de autenticação (401 → token refresh → retry).
 * Se refresh falha: limpa tokens, redireciona para login.
 *
 * Segue o padrão do admin frontend (frontend/admin/src/lib/api.ts)
 * com adição de token refresh automático.
 */

import type { ApiResponse } from '@/types/api';

const API_URL = import.meta.env.VITE_API_URL ?? '';

const TOKEN_KEY = 'bolao_token';
const REFRESH_TOKEN_KEY = 'bolao_refresh_token';
const USER_KEY = 'bolao_user';

/**
 * Refresh lock — prevents multiple simultaneous refresh attempts.
 * When a refresh is in progress, subsequent 401s wait for the same promise.
 */
let refreshPromise: Promise<string> | null = null;

function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function redirectToLogin(): void {
  window.location.href = '/login';
}

/**
 * Attempts to refresh the access token using the stored refresh token.
 * Uses a lock so only one refresh runs at a time — concurrent 401s
 * share the same refresh promise.
 */
async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const json: ApiResponse<{ accessToken: string; refreshToken?: string }> =
      await response.json();

    if (!json.success || !json.data?.accessToken) {
      throw new Error('Token refresh failed');
    }

    localStorage.setItem(TOKEN_KEY, json.data.accessToken);
    if (json.data.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, json.data.refreshToken);
    }

    return json.data.accessToken;
  })();

  try {
    const token = await refreshPromise;
    return token;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Core request function.
 * - Injects Authorization header with current access token
 * - On 401: attempts token refresh and retries the original request once
 * - On refresh failure: clears auth state and redirects to login
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAccessToken();

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    try {
      const newToken = await refreshAccessToken();

      const retryResponse = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${newToken}`,
          ...options.headers,
        },
      });

      if (retryResponse.status === 204) {
        return undefined as T;
      }

      const retryJson: ApiResponse<T> = await retryResponse.json();

      if (!retryResponse.ok || !retryJson.success) {
        throw new Error(retryJson.error?.message ?? `Erro ${retryResponse.status}`);
      }

      return retryJson.data as T;
    } catch {
      clearAuth();
      redirectToLogin();
      throw new Error('Sessão expirada');
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const json: ApiResponse<T> = await response.json();

  if (!response.ok || !json.success) {
    throw new Error(json.error?.message ?? `Erro ${response.status}`);
  }

  return json.data as T;
}

const api = {
  get: <T>(path: string) => request<T>(path),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),
};

export default api;
